<template>
  <section>
    <action-bar :light="true">
      <ul class="top-countries">
        <li v-if="topCountries" v-for="country in topCountries" @click="selectCountry(country.code)" :class="{ saving: isSaving && country.code === selectedCountry, selected: country.code === selectedCountry }">
          <span class="flag flag-icon flag-icon-squared" :class="'flag-icon-' + country.code.toLowerCase()"></span>
          {{ getLocalCountryName(country.code, i18n) }}
        </li>
      </ul>
      <div class="country-group-separator">
        <div class="separator-container">
          {{ i18n.t('country:group_separator_all') }}
        </div>
      </div>
      <ul class="other-countries">
        <li v-for="country in countries" @click="selectCountry(country.code)" :class="{ saving: isSaving && country.code === selectedCountry, selected: country.code === selectedCountry }">
          {{ country.name }}
        </li>
      </ul>
    </action-bar>
  </section>
</template>

<style lang="scss" rel="stylesheet/scss" scoped>
  @import '../assets/_settings';
  @import '../assets/mixins';
  @import '../assets/country-flags';

  .actions{
    display: block;
    background: none;
    top:0;
    position: relative;
    width: 100%;
    padding: 0 30px 30px;

    @include desktop-big {
      width: 380px;
      left: 0;
      margin: 0 auto;
    }
  }

  ul {
    @include button-list;
    overflow: visible;
    display: block;

    li{
      height: 40px !important;
      line-height: 40px !important;
      padding: 0 0 0 30px !important;
      box-shadow: none;
      border-radius: 0;
      font-size: 1rem !important;
      font-weight: 400 !important;
      @include tablet {
        padding-left: 0!important;
      }

      .flag,
      .flag.flag-icon-squared {
        display: inline-block;
        background-repeat: no-repeat;
        width:24px;
        height: 24px;
        vertical-align: super;
        margin-right: 14px;
        top: 2px;
        border-radius: 50%;
      }
    }

    &.top-countries{
      padding: 10px 0;

      @include desktop-big {
        li {
          background-color: transparent;
          box-shadow: none;
          border-color: transparent;
        }
      }
    }

    @include desktop-big {
      &.other-countries {
        li {
          background-color: transparent;
          box-shadow: none;
          border-color: transparent;
        }
      }
    }
  }

  .country-group-separator{
    background: #F6F8FB;
    color: #C2C3C5;
    font-family: $countryModalFontFamily;
    font-family: $countryModalFontFamily-portal;
    font-weight: 400;
    font-size: .875rem;
    line-height: 30px;
    padding: 0 30px;
    height: 30px;
    margin: 15px auto;
    display: flex;
    @include tablet {
      justify-content: center;
    }
  }
  .separator-container {
    @include tablet {
      width: $tablet-content-width;
    }
  }
  input {
    &::-webkit-input-placeholder {
      font-weight: 400;
    }
    &[disabled] {
      color: black;
    }

    border-bottom-color: transparent;
  }
  .question{
    bottom: 20%;
  }
</style>

<script>
  import Vue from 'vue'
  import ActionBar from '../components/ActionBar'
  import { mapState } from 'vuex'
  import { getNestedProperty } from '../utils/structure.js'

  export default Vue.component('country-select', {
    props: ['screenParams', 'nationality', 'onSelect'],
    components: {
      'action-bar': ActionBar
    },
    data () {
      const key = this.screenParams && this.screenParams.dispatchKey ? this.screenParams.dispatchKey.replace('update', '').toLowerCase() : 'user'
      let propertyName = (this.screenParams && this.screenParams.propertyName) || (this.nationality && 'nationality') || 'country'
      propertyName = propertyName.split('.')
      propertyName = propertyName.length > 1 ? propertyName.slice(0, -1).join('.') + (this.nationality && '.nationality') || '.country' : (this.nationality && 'nationality') || 'country'
      const currentValue = getNestedProperty(this.$store.state[key], propertyName)

      return {
        changedCountry: null,
        isSaving: false,
        currentCountry: currentValue,
        propertyName: propertyName,
        dispatchKey: (this.screenParams && this.screenParams.dispatchKey) || 'updateUser'
      }
    },
    computed: {
      ...mapState(['l10n', 'user', 'app', 'partner', 'location']),
      countries () {
        let filteredCountries = []

        for (let countryCode of this.app.supportedCountries) {
          let isRestricted = this.isRestrictedCountry(countryCode)

          if (!isRestricted) {
            // Maps country code instead of loading every country, because the selecotr uses only country code
            let country = !!this.l10n[countryCode] && {
              code: countryCode,
              name: this.getLocalCountryName(countryCode, this.i18n)
            }
            let isFeaturedCountry = this.partner.featuredCountries.indexOf(countryCode) !== -1

            if (country && country !== null && country !== undefined && !isFeaturedCountry) filteredCountries.push(country)
          }
        }

        return filteredCountries.sort(this.sortAlphebetically)
      },
      topCountries () {
        let filteredCountries = []

        for (let countryCode of this.partner.featuredCountries) {
          let isRestricted = this.isRestrictedCountry(countryCode)

          if (!isRestricted) {
            let country = !!this.l10n[countryCode] && {
              code: countryCode
            }

            if (country && country !== null && country !== undefined && country.code === this.location.countryCode) {
              filteredCountries.unshift(country)
            } else if (country && country !== null && country !== undefined) {
              filteredCountries.push(country)
            }
          }
        }

        return filteredCountries
      },
      selectedCountry () {
        return this.changedCountry || this.currentCountry || this.app.country.code
      }
    },
    methods: {
      async selectCountry (countryCode) {
        this.isSaving = true
        this.changedCountry = countryCode

        let dispatchData = {}
        dispatchData[this.propertyName] = countryCode

        await this.$store.dispatch(this.dispatchKey, dispatchData)

        if (!this.nationality) {
          this.$store.dispatch('updateApp', {
            country: countryCode
          })
        }

        if (this.onSelect) {
          return this.onSelect({
            countryCode: countryCode
          })
        }

        setTimeout(() => {
          this.$emit('closeModal')
          this.isSaving = false
        }, 200)
      },
      sortAlphebetically (a, b) {
        if (a.name < b.name) return -1
        if (a.name > b.name) return 1
        return 0
      },
      isRestrictedCountry (countryCode) {
        return this.partner.restrictedCountries ? this.partner.restrictedCountries.indexOf(countryCode) !== -1 : false
      }
    }
  })
</script>



// WEBPACK FOOTER //
// src/components/Country.vue